import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faUsers, faUser, faUserTag, faStar, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import '../styles/Notifications.css';

const NotificationForm = ({ onClose }) => {
    const [formData, setFormData] = useState({
        title: '',
        message: '',
        type: 'info',
        recipient_type: 'all',
        recipients: [],
        important: false
    });
    
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        fetchUsersAndRoles();
    }, []);

    const fetchUsersAndRoles = async () => {
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_users.php');
            const data = await response.json();
            credentials: 'include'
            if (data.success) {
                setUsers(data.users || []);
                setRoles(data.roles || []);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        
        // Clear recipients when changing recipient type
        if (name === 'recipient_type') {
            setFormData(prev => ({ ...prev, recipients: [] }));
        }
    };

    const handleRecipientToggle = (value) => {
        setFormData(prev => {
            const recipients = [...prev.recipients];
            const index = recipients.indexOf(value);
            
            if (index > -1) {
                recipients.splice(index, 1);
            } else {
                recipients.push(value);
            }
            
            return { ...prev, recipients };
        });
    };

    const handleSelectAllUsers = () => {
        if (formData.recipient_type === 'specific') {
            const allUsernames = users.map(user => user.username);
            setFormData(prev => ({ ...prev, recipients: allUsernames }));
        }
    };

    const handleDeselectAll = () => {
        setFormData(prev => ({ ...prev, recipients: [] }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title.trim() || !formData.message.trim()) {
            alert('Please fill in title and message');
            return;
        }
        
        if (formData.recipient_type !== 'all' && formData.recipients.length === 0) {
            alert('Please select at least one recipient');
            return;
        }
        
        setSending(true);
        
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/notifications.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
                credentials: 'include'
            });
            
            const data = await response.json();
            
            if (data.success) {
                setSuccess(true);
                setTimeout(() => {
                    setSuccess(false);
                    onClose();
                }, 2000);
            } else {
                alert(data.message || 'Failed to send notification');
            }
        } catch (error) {
            console.error('Error sending notification:', error);
            alert('Error sending notification');
        } finally {
            setSending(false);
        }
    };

    if (loading) {
        return (
            <div className="notification-form">
                <div className="notification-form__loading">
                    <div className="notification-form__spinner"></div>
                    <p>Loading users...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="notification-form">
            <div className="notification-form__header">
                <h3>
                    <FontAwesomeIcon icon={faPaperPlane} />
                    Send Notification
                </h3>
                <button className="notification-form__close-btn" onClick={onClose}>×</button>
            </div>
            
            {success && (
                <div className="notification-form__success">
                    ✓ Notification sent successfully!
                </div>
            )}
            
            <form onSubmit={handleSubmit} className="notification-form__form">
                <div className="notification-form__group">
                    <label>Title *</label>
                    <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="Enter notification title"
                        maxLength="255"
                        required
                    />
                </div>
                
                <div className="notification-form__group">
                    <label>Message *</label>
                    <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Enter notification message"
                        rows="4"
                        required
                    />
                </div>
                
                <div className="notification-form__row">
                    <div className="notification-form__group">
                        <label>Type</label>
                        <select
                            name="type"
                            value={formData.type}
                            onChange={handleInputChange}
                        >
                            <option value="info">Info</option>
                            <option value="success">Success</option>
                            <option value="warning">Warning</option>
                            <option value="error">Error</option>
                            <option value="system">System</option>
                        </select>
                    </div>
                    
                    <div className="notification-form__group">
                        <label className="notification-form__checkbox-label">
                            <input
                                type="checkbox"
                                name="important"
                                checked={formData.important}
                                onChange={handleInputChange}
                            />
                            <FontAwesomeIcon icon={faStar} /> Mark as Important
                        </label>
                    </div>
                </div>
                
                <div className="notification-form__group">
                    <label>Send To</label>
                    <div className="notification-form__recipient-type">
                        <div className="notification-form__type-options">
                            <button
                                type="button"
                                className={`notification-form__type-option ${formData.recipient_type === 'all' ? 'notification-form__type-option--active' : ''}`}
                                onClick={() => setFormData(prev => ({ ...prev, recipient_type: 'all', recipients: [] }))}
                            >
                                <FontAwesomeIcon icon={faUsers} />
                                <span>All Users</span>
                                <small>{users.length} users</small>
                            </button>
                            
                            <button
                                type="button"
                                className={`notification-form__type-option ${formData.recipient_type === 'specific' ? 'notification-form__type-option--active' : ''}`}
                                onClick={() => setFormData(prev => ({ ...prev, recipient_type: 'specific', recipients: [] }))}
                            >
                                <FontAwesomeIcon icon={faUser} />
                                <span>Specific Users</span>
                                <small>Select individual users</small>
                            </button>
                            
                            <button
                                type="button"
                                className={`notification-form__type-option ${formData.recipient_type === 'role_based' ? 'notification-form__type-option--active' : ''}`}
                                onClick={() => setFormData(prev => ({ ...prev, recipient_type: 'role_based', recipients: [] }))}
                            >
                                <FontAwesomeIcon icon={faUserTag} />
                                <span>By Role</span>
                                <small>Select user roles</small>
                            </button>
                        </div>
                    </div>
                </div>
                
                {formData.recipient_type === 'specific' && (
                    <div className="notification-form__group notification-form__recipient-selection">
                        <div className="notification-form__selection-header">
                            <label>Select Users ({formData.recipients.length} selected)</label>
                            <div className="notification-form__selection-actions">
                                <button type="button" onClick={handleSelectAllUsers}>Select All</button>
                                <button type="button" onClick={handleDeselectAll}>Clear</button>
                            </div>
                        </div>
                        <div className="notification-form__users-list">
                            {users.map(user => (
                                <div key={user.id} className="notification-form__user-item">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={formData.recipients.includes(user.username)}
                                            onChange={() => handleRecipientToggle(user.username)}
                                        />
                                        <div className="notification-form__user-info">
                                            <span className="notification-form__username">{user.username}</span>
                                            <small>{user.fullname} • {user.role}</small>
                                        </div>
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                
                {formData.recipient_type === 'role_based' && (
                    <div className="notification-form__group notification-form__recipient-selection">
                        <div className="notification-form__selection-header">
                            <label>Select Roles ({formData.recipients.length} selected)</label>
                            <div className="notification-form__selection-actions">
                                <button type="button" onClick={() => setFormData(prev => ({ ...prev, recipients: [...roles] }))}>
                                    Select All
                                </button>
                                <button type="button" onClick={handleDeselectAll}>Clear</button>
                            </div>
                        </div>
                        <div className="notification-form__roles-grid">
                            {roles.map(role => (
                                <div key={role} className="notification-form__role-item">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={formData.recipients.includes(role)}
                                            onChange={() => handleRecipientToggle(role)}
                                        />
                                        <span>{role}</span>
                                        <small>
                                            {users.filter(u => u.role === role).length} users
                                        </small>
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                
                <div className="notification-form__footer">
                    <button type="button" className="notification-form__cancel-btn" onClick={onClose}>
                        Cancel
                    </button>
                    <button type="submit" className="notification-form__send-btn" disabled={sending}>
                        {sending ? 'Sending...' : (
                            <>
                                <FontAwesomeIcon icon={faPaperPlane} />
                                Send Notification
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default NotificationForm;